import React, { useState, useRef, useEffect, useContext } from "react";
import { Drawer } from "../ui/Drawer";
import { usePageBuilder } from "../../context/PageBuilderContext";
import { Input } from "../ui/Input";
import { RichTextEditor } from "../ui/RichTextEditor";
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaUpload,
  FaImage,
  FaCheck,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaStore,
  FaCheckCircle,
  FaGlobe,
  FaShieldAlt,
  FaChartLine,
  FaPlug,
  FaHeadset,
  FaCreditCard,
  FaInfinity,
  FaRocket,
  FaTimes,
} from "react-icons/fa";
import * as FaIcons from "react-icons/fa";
import { useApi } from "../../context/ApiContext";
import { IComponentNode } from "../../types/pageBuilder.types";
import { getOriginalImageUrl } from "../../utils/imageUtils";
import { TranslatableField } from "../common/TranslatableField";
import { SupportedLanguage } from "../../types/translations.types";
import { useModalLanguage } from "../../context/ModalLanguageContext";
import { FontSelector } from "../productCustomization/FontSelector";
import { DataSourceType, DisplayComponentType, IFilterConfig } from "../../types/pageBuilder.types";
import { StoreContext } from "../../context/StoreContext";

interface PropertyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ImageComponentEditorProps {
  component: IComponentNode;
  onPropChange: (key: string, value: any) => void;
}

interface IconComponentEditorProps {
  component: IComponentNode;
  onPropChange: (key: string, value: any) => void;
}

// Popular Font Awesome icons for quick selection
const popularIcons = [
  { name: "FaCheck", label: "Check", icon: FaCheck },
  { name: "FaCheckCircle", label: "Check Circle", icon: FaCheckCircle },
  { name: "FaUser", label: "User", icon: FaUser },
  { name: "FaEnvelope", label: "Envelope", icon: FaEnvelope },
  { name: "FaPhone", label: "Phone", icon: FaPhone },
  { name: "FaMapMarkerAlt", label: "Location", icon: FaMapMarkerAlt },
  { name: "FaStore", label: "Store", icon: FaStore },
  { name: "FaGlobe", label: "Globe", icon: FaGlobe },
  { name: "FaShieldAlt", label: "Shield", icon: FaShieldAlt },
  { name: "FaChartLine", label: "Chart", icon: FaChartLine },
  { name: "FaPlug", label: "Plugin", icon: FaPlug },
  { name: "FaHeadset", label: "Support", icon: FaHeadset },
  { name: "FaCreditCard", label: "Credit Card", icon: FaCreditCard },
  { name: "FaInfinity", label: "Infinity", icon: FaInfinity },
  { name: "FaRocket", label: "Rocket", icon: FaRocket },
];

const IconComponentEditor: React.FC<IconComponentEditorProps> = ({
  component,
  onPropChange,
}) => {
  const iconName = component.props.iconName || "FaCheck";
  const iconColor = component.props.color || "#4F46E5";
  const iconSize = component.props.size || "24";
  const [searchTerm, setSearchTerm] = useState("");

  // Filter icons based on search
  const filteredIcons = popularIcons.filter(
    (icon) =>
      icon.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      icon.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Icon Preview</label>
        <div className="flex items-center justify-center p-4 border border-gray-300 bg-gray-50">
          {(() => {
            const IconComponent = (FaIcons as any)[iconName] || FaCheck;
            return (
              <IconComponent
                size={parseInt(iconSize)}
                style={{ color: iconColor }}
              />
            );
          })()}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Search Icon</label>
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search icons..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Select Icon</label>
        <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-300 p-2">
          {filteredIcons.map((icon) => {
            const IconComponent = icon.icon;
            return (
              <button
                key={icon.name}
                type="button"
                onClick={() => onPropChange("iconName", icon.name)}
                className={`p-2 border rounded hover:bg-gray-100 flex flex-col items-center gap-1 ${
                  iconName === icon.name
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300"
                }`}
                title={icon.label}
              >
                <IconComponent size={20} style={{ color: iconColor }} />
                <span className="text-xs text-gray-600 truncate w-full">
                  {icon.label}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Or enter icon name manually (e.g., FaCheck, FaUser, FaEnvelope)
        </p>
        <Input
          value={iconName}
          onChange={(e) => onPropChange("iconName", e.target.value)}
          placeholder="FaCheck"
          className="mt-2"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Icon Color</label>
        <div className="flex gap-2">
          <Input
            type="color"
            value={iconColor}
            onChange={(e) => onPropChange("color", e.target.value)}
            className="w-16 h-10"
          />
          <Input
            value={iconColor}
            onChange={(e) => onPropChange("color", e.target.value)}
            placeholder="#4F46E5"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Icon Size (px)</label>
        <Input
          type="number"
          value={iconSize}
          onChange={(e) => onPropChange("size", e.target.value)}
          placeholder="24"
          min="8"
          max="128"
        />
      </div>
    </div>
  );
};

const ImageComponentEditor: React.FC<ImageComponentEditorProps> = ({
  component,
}) => {
  const { updateComponent, layout, findComponent } = usePageBuilder();
  const { currentLanguage } = useModalLanguage();

  // Get the latest component data from the layout tree to ensure we have current values
  // Recalculate on every render to get the latest data
  const currentComponent = layout
    ? findComponent(component.id, layout) || component
    : component;

  const handleStyleChange = (breakpoint: string, key: string, value: any) => {
    const currentStyles = currentComponent.styles || {};
    const breakpointStyles =
      currentStyles[breakpoint as keyof typeof currentStyles] || {};
    const updatedBreakpointStyles =
      value === undefined
        ? Object.fromEntries(
            Object.entries(breakpointStyles).filter(([k]) => k !== key)
          )
        : { ...breakpointStyles, [key]: value };
    updateComponent(currentComponent.id, {
      styles: {
        ...currentStyles,
        [breakpoint]: updatedBreakpointStyles,
      },
    });
  };

  // Create a wrapper for onPropChange that uses the current component ID
  const handlePropChange = (key: string, value: any) => {
    // Update props using the current component
    updateComponent(currentComponent.id, {
      props: {
        ...currentComponent.props,
        [key]: value,
      },
    });
  };

  // Handle translation change for image src
  const handleImageTranslationChange = (
    language: SupportedLanguage,
    imageUrl: string
  ) => {
    if (language === "en") {
      // For English, update the main prop value
      handlePropChange("src", imageUrl);
    } else {
      // For other languages, update translations
      const translations = currentComponent.translations || {};
      const srcTranslations = translations.src || {};
      const updatedTranslations = {
        ...translations,
        src: {
          ...srcTranslations,
          [language]: imageUrl,
        },
      };
      updateComponent(currentComponent.id, {
        translations: updatedTranslations,
      });
    }
  };
  const { api } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const file = files[0];
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/admin/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.url) {
        // Store the relative path - it will be converted to full URL when rendering
        // This allows the path to work both in the builder and in the rendered page
        setImageUrl(response.data.url);
        // If non-English language is selected, store as translation
        handleImageTranslationChange(currentLanguage, response.data.url);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // Get translated image URL based on current language
  const getTranslatedImageUrl = (): string => {
    if (currentLanguage === "en") {
      return currentComponent.props.src || "";
    }
    const translations = currentComponent.translations?.src;
    if (translations && translations[currentLanguage]) {
      return translations[currentLanguage];
    }
    return currentComponent.props.src || "";
  };

  // Use local state for input values to ensure they're editable
  // Initialize from currentComponent props or translations
  const [imageUrl, setImageUrl] = React.useState(() => getTranslatedImageUrl());
  const [altText, setAltText] = React.useState(
    () => currentComponent.props.alt || ""
  );
  const [width, setWidth] = React.useState(
    () => currentComponent.props.width || ""
  );
  const [height, setHeight] = React.useState(
    () => currentComponent.props.height || ""
  );
  const [maxWidth, setMaxWidth] = React.useState(
    () => currentComponent.props.maxWidth || ""
  );
  const [maxHeight, setMaxHeight] = React.useState(
    () => currentComponent.props.maxHeight || ""
  );
  const [objectFit, setObjectFit] = React.useState(
    () => currentComponent.props.objectFit || "cover"
  );
  const [loading, setLoading] = React.useState(
    () =>
      currentComponent.props.loading === "lazy" ||
      currentComponent.props.loading === true
  );

  // Sync local state with component props only when component ID or language changes
  // We use a ref to track the component ID and only sync when it changes
  const prevComponentIdRef = React.useRef<string>(currentComponent.id);
  const prevLanguageRef = React.useRef<SupportedLanguage>(currentLanguage);

  React.useEffect(() => {
    // Sync when component ID changes or language changes
    if (
      prevComponentIdRef.current !== currentComponent.id ||
      prevLanguageRef.current !== currentLanguage
    ) {
      prevComponentIdRef.current = currentComponent.id;
      prevLanguageRef.current = currentLanguage;
      const props = currentComponent.props;
      setImageUrl(getTranslatedImageUrl());
      setAltText(props.alt || "");
      setWidth(props.width || "");
      setHeight(props.height || "");
      setMaxWidth(props.maxWidth || "");
      setMaxHeight(props.maxHeight || "");
      setObjectFit(props.objectFit || "cover");
      setLoading(props.loading === "lazy" || props.loading === true);
    }
  }, [currentComponent.id, currentLanguage]); // Sync when component ID or language changes

  const hasImage = imageUrl && imageUrl.trim() !== "";

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Upload Image</label>
        <div
          className={`border-2 border-dashed p-4 text-center transition-colors ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
          <FaImage className="mx-auto text-2xl text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Drag and drop image here, or click to select
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
            disabled={uploading}
          >
            <FaUpload />
            {uploading ? "Uploading..." : "Select Image"}
          </button>
        </div>
      </div>

      {hasImage && (
        <div>
          <label className="block text-sm font-medium mb-2">Preview</label>
          <div className="border border-gray-200 p-2 bg-gray-50">
            <img
              src={
                imageUrl.startsWith("http")
                  ? imageUrl
                  : getOriginalImageUrl(imageUrl)
              }
              alt={currentComponent.props.alt || "Preview"}
              className="max-w-full h-auto max-h-48 mx-auto rounded"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Image URL</label>
        <Input
          value={imageUrl}
          onChange={(e) => {
            setImageUrl(e.target.value);
            handleImageTranslationChange(currentLanguage, e.target.value);
          }}
          placeholder="https://example.com/image.jpg or upload above"
        />
        <p className="text-xs text-gray-500 mt-1">
          You can upload an image above or enter an image URL directly
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Alt Text</label>
        <Input
          value={altText}
          onChange={(e) => {
            setAltText(e.target.value);
            handlePropChange("alt", e.target.value);
          }}
          placeholder="Image description"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Width</label>
          <Input
            value={width}
            onChange={(e) => {
              setWidth(e.target.value);
              handlePropChange("width", e.target.value);
              handleStyleChange("default", "width", e.target.value);
            }}
            placeholder="auto, 100%, 300px"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Height</label>
          <Input
            value={height}
            onChange={(e) => {
              setHeight(e.target.value);
              handlePropChange("height", e.target.value);
              handleStyleChange("default", "height", e.target.value);
            }}
            placeholder="auto, 200px"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Max Width</label>
          <Input
            value={maxWidth}
            onChange={(e) => {
              setMaxWidth(e.target.value);
              handlePropChange("maxWidth", e.target.value);
              handleStyleChange("default", "maxWidth", e.target.value);
            }}
            placeholder="100%, 800px"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Max Height</label>
          <Input
            value={maxHeight}
            onChange={(e) => {
              setMaxHeight(e.target.value);
              handlePropChange("maxHeight", e.target.value);
              handleStyleChange("default", "maxHeight", e.target.value);
            }}
            placeholder="600px"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Object Fit</label>
        <select
          value={objectFit}
          onChange={(e) => {
            setObjectFit(e.target.value);
            handlePropChange("objectFit", e.target.value);
            handleStyleChange("default", "objectFit", e.target.value);
          }}
          className="w-full px-3 py-2 border border-gray-300 "
        >
          <option value="fill">Fill</option>
          <option value="contain">Contain</option>
          <option value="cover">Cover</option>
          <option value="none">None</option>
          <option value="scale-down">Scale Down</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Lazy Loading</label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={loading}
            onChange={(e) => {
              setLoading(e.target.checked);
              handlePropChange("loading", e.target.checked ? "lazy" : "eager");
            }}
            className="rounded border-gray-300"
          />
          <span className="text-sm">Enable lazy loading</span>
        </label>
      </div>
    </div>
  );
};

// Helper component for style inputs with reset
const StyleInputWithReset: React.FC<{
  label: string;
  value: any;
  onChange: (value: any) => void;
  onReset: () => void;
  placeholder?: string;
  type?: string;
  children?: React.ReactNode;
}> = ({ label, value, onChange, onReset, placeholder, type, children }) => {
  const hasValue = value !== undefined && value !== null && value !== "";
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium">{label}</label>
        {hasValue && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onReset();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
            title="Reset to default"
          >
            <FaTimes className="text-xs" />
          </button>
        )}
      </div>
      {children || (
        <Input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
};

export const PropertyDrawer: React.FC<PropertyDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { selectedComponent, updateComponent, layout, findComponent } =
    usePageBuilder();
  const [activeTab, setActiveTab] = useState<
    "content" | "style" | "layout" | "animation" | "data"
  >("content");
  // Use StoreContext directly to handle cases where StoreProvider might not be available
  // (e.g., in platform zone editing context)
  const storeContext = useContext(StoreContext);
  const store = storeContext?.store || null;
  const { api } = useApi();
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Auto-switch to Data tab when dynamic-snippet is selected
  useEffect(() => {
    if (selectedComponent?.type === "dynamic-snippet" && activeTab === "content") {
      setActiveTab("data");
    }
  }, [selectedComponent?.type, selectedComponent?.id]);

  // Load categories when data source is products
  useEffect(() => {
    if (selectedComponent?.type === "dynamic-snippet" && selectedComponent.props?.dataSource === "products" && store?._id) {
      const loadCategories = async () => {
        try {
          setLoadingCategories(true);
          const response = await api.get(`/categories?storeId=${store?._id}`);
          setCategories(response.data.categories || []);
        } catch (error) {
          console.error("Error loading categories:", error);
        } finally {
          setLoadingCategories(false);
        }
      };
      loadCategories();
    }
  }, [selectedComponent?.type, selectedComponent?.props?.dataSource, store?._id, api]);

  // Get the latest component data from the layout tree to ensure we have current values
  const currentComponent =
    selectedComponent && layout
      ? findComponent(selectedComponent.id, layout) || selectedComponent
      : selectedComponent;

  if (!currentComponent) {
    return null;
  }

  const handlePropChange = (key: string, value: any) => {
    if (!currentComponent) return;
    updateComponent(currentComponent.id, {
      props: {
        ...currentComponent.props,
        [key]: value,
      },
    });
  };

  const handleTranslationChange = (
    propName: string,
    language: SupportedLanguage,
    value: string
  ) => {
    if (!currentComponent) return;
    const translations = currentComponent.translations || {};
    const propTranslations = translations[propName] || {};

    if (language === "en") {
      // For English, update the main prop value
      handlePropChange(propName, value);
    } else {
      // For other languages, update translations
      const updatedTranslations = {
        ...translations,
        [propName]: {
          ...propTranslations,
          [language]: value,
        },
      };
      updateComponent(currentComponent.id, {
        translations: updatedTranslations,
      });
    }
  };

  const handleStyleChange = (breakpoint: string, key: string, value: any) => {
    if (!currentComponent) return;
    const currentStyles = currentComponent.styles || {};
    const breakpointStyles =
      currentStyles[breakpoint as keyof typeof currentStyles] || {};

    // If value is undefined, remove the key from the breakpoint styles
    let updatedBreakpointStyles: Record<string, any>;
    if (value === undefined) {
      // Remove the key from breakpoint styles
      const { [key]: _, ...rest } = breakpointStyles;
      updatedBreakpointStyles = rest;
    } else {
      updatedBreakpointStyles = { ...breakpointStyles, [key]: value };
    }

    // If the breakpoint styles object is empty after removal, we can optionally remove the entire breakpoint
    // But for now, we'll keep the empty object to maintain the breakpoint structure
    const updatedStyles = {
      ...currentStyles,
      [breakpoint]: updatedBreakpointStyles,
    };

    updateComponent(currentComponent.id, {
      styles: updatedStyles,
    });
  };

  const defaultStyles = currentComponent.styles?.default || {};

  const renderContentTab = () => {
    if (!currentComponent) return null;
    switch (currentComponent.type) {
      case "heading":
        return (
          <div className="space-y-4">
            <div>
              <TranslatableField
                label="Heading Text"
                fieldName="text"
                entity={{
                  text: currentComponent.props.text || "",
                  translations: currentComponent.translations || {},
                }}
                value={currentComponent.props.text || ""}
                onChange={(language, value) =>
                  handleTranslationChange("text", language, value)
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Level</label>
              <select
                value={currentComponent.props.level || 1}
                onChange={(e) =>
                  handlePropChange("level", parseInt(e.target.value))
                }
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
                <option value={4}>H4</option>
                <option value={5}>H5</option>
                <option value={6}>H6</option>
              </select>
            </div>
          </div>
        );
      case "text":
        return (
          <div className="space-y-4">
            <div>
              <TranslatableField
                label="Text Content"
                fieldName="text"
                entity={{
                  text: currentComponent.props.text || "",
                  translations: currentComponent.translations || {},
                }}
                value={currentComponent.props.text || ""}
                onChange={(language, value) =>
                  handleTranslationChange("text", language, value)
                }
                type="rich"
                height="300px"
              />
            </div>
          </div>
        );
      case "list":
      case "ordered-list":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                List Items (one per line)
              </label>
              <textarea
                value={(currentComponent.props.items || []).join("\n")}
                onChange={(e) => {
                  const items = e.target.value
                    .split("\n")
                    .filter((item) => item.trim() !== "");
                  handlePropChange("items", items);
                }}
                className="w-full px-3 py-2 border border-gray-300 "
                rows={6}
                placeholder="Item 1&#10;Item 2&#10;Item 3"
              />
            </div>
          </div>
        );
      case "quote":
        return (
          <div className="space-y-4">
            <div>
              <TranslatableField
                label="Quote Text"
                fieldName="text"
                entity={{
                  text: currentComponent.props.text || "",
                  translations: currentComponent.translations || {},
                }}
                value={currentComponent.props.text || ""}
                onChange={(language, value) =>
                  handleTranslationChange("text", language, value)
                }
                type="rich"
              />
            </div>
            <div>
              <TranslatableField
                label="Author (optional)"
                fieldName="author"
                entity={{
                  author: currentComponent.props.author || "",
                  translations: currentComponent.translations || {},
                }}
                value={currentComponent.props.author || ""}
                onChange={(language, value) =>
                  handleTranslationChange("author", language, value)
                }
              />
            </div>
          </div>
        );
      case "code":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Code</label>
              <textarea
                value={currentComponent.props.code || ""}
                onChange={(e) => handlePropChange("code", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 font-mono"
                rows={10}
                placeholder="// Your code here"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Language</label>
              <select
                value={currentComponent.props.language || "javascript"}
                onChange={(e) => handlePropChange("language", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="json">JSON</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="php">PHP</option>
                <option value="ruby">Ruby</option>
                <option value="go">Go</option>
                <option value="rust">Rust</option>
                <option value="bash">Bash</option>
                <option value="sql">SQL</option>
                <option value="plaintext">Plain Text</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Show Line Numbers
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.showLineNumbers || false}
                  onChange={(e) =>
                    handlePropChange("showLineNumbers", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Display line numbers</span>
              </label>
            </div>
          </div>
        );
      case "card":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Card Title
              </label>
              <Input
                value={currentComponent.props.title || ""}
                onChange={(e) => handlePropChange("title", e.target.value)}
                placeholder="Card Title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Card Content
              </label>
              <RichTextEditor
                value={currentComponent.props.content || ""}
                onChange={(value) => handlePropChange("content", value)}
                height="200px"
              />
            </div>
          </div>
        );
      case "link":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Link Text
              </label>
              <Input
                value={currentComponent.props.text || ""}
                onChange={(e) => handlePropChange("text", e.target.value)}
                placeholder="Link Text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL</label>
              <Input
                value={currentComponent.props.href || ""}
                onChange={(e) => handlePropChange("href", e.target.value)}
                placeholder="/page or https://example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Link Target
              </label>
              <select
                value={currentComponent.props.target || "_self"}
                onChange={(e) => handlePropChange("target", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="_self">Same Window</option>
                <option value="_blank">New Window</option>
                <option value="_parent">Parent Frame</option>
                <option value="_top">Top Frame</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Rel Attribute
              </label>
              <Input
                value={currentComponent.props.rel || ""}
                onChange={(e) => handlePropChange("rel", e.target.value)}
                placeholder="noopener, noreferrer"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: "noopener noreferrer" for external links
              </p>
            </div>
          </div>
        );
      case "divider":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Divider Style
              </label>
              <select
                value={currentComponent.props.style || "solid"}
                onChange={(e) => {
                  handlePropChange("style", e.target.value);
                  handleStyleChange("default", "borderStyle", e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="double">Double</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Thickness
              </label>
              <Input
                value={currentComponent.props.thickness || "1px"}
                onChange={(e) => {
                  handlePropChange("thickness", e.target.value);
                  handleStyleChange("default", "height", e.target.value);
                  handleStyleChange(
                    "default",
                    "borderTopWidth",
                    e.target.value
                  );
                }}
                placeholder="1px, 2px, 4px"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={currentComponent.props.color || "#e5e7eb"}
                  onChange={(e) => {
                    handlePropChange("color", e.target.value);
                    handleStyleChange(
                      "default",
                      "backgroundColor",
                      e.target.value
                    );
                    handleStyleChange("default", "borderColor", e.target.value);
                  }}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={currentComponent.props.color || "#e5e7eb"}
                  onChange={(e) => {
                    handlePropChange("color", e.target.value);
                    handleStyleChange(
                      "default",
                      "backgroundColor",
                      e.target.value
                    );
                    handleStyleChange("default", "borderColor", e.target.value);
                  }}
                  placeholder="#e5e7eb"
                />
              </div>
            </div>
          </div>
        );
      case "image":
        return (
          <ImageComponentEditor
            key={currentComponent.id}
            component={currentComponent}
            onPropChange={handlePropChange}
          />
        );
      case "icon":
        return (
          <IconComponentEditor
            key={currentComponent.id}
            component={currentComponent}
            onPropChange={handlePropChange}
          />
        );
      case "button":
        return (
          <div className="space-y-4">
            <div>
              <TranslatableField
                label="Button Text"
                fieldName="text"
                entity={{
                  text: currentComponent.props.text || "",
                  translations: currentComponent.translations || {},
                }}
                value={currentComponent.props.text || ""}
                onChange={(language, value) =>
                  handleTranslationChange("text", language, value)
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Link URL</label>
              <Input
                value={currentComponent.props.href || ""}
                onChange={(e) => handlePropChange("href", e.target.value)}
                placeholder="/page or https://example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Button Type
              </label>
              <select
                value={currentComponent.props.type || "button"}
                onChange={(e) => handlePropChange("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="button">Button</option>
                <option value="submit">Submit</option>
                <option value="reset">Reset</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Link Target
              </label>
              <select
                value={currentComponent.props.target || "_self"}
                onChange={(e) => handlePropChange("target", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="_self">Same Window</option>
                <option value="_blank">New Window</option>
                <option value="_parent">Parent Frame</option>
                <option value="_top">Top Frame</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Disabled</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.disabled || false}
                  onChange={(e) =>
                    handlePropChange("disabled", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Disable button</span>
              </label>
            </div>
          </div>
        );
      case "grid":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Columns</label>
              <input
                type="number"
                value={String(currentComponent.props?.columns ?? 3)}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const inputValue = e.target.value;
                  if (inputValue === "") {
                    // Allow empty input temporarily
                    return;
                  }
                  const cols = parseInt(inputValue, 10);
                  if (!isNaN(cols) && cols >= 1 && cols <= 12) {
                    // Update both props and styles in a single call to avoid race conditions
                    const updatedProps = {
                      ...currentComponent.props,
                      columns: cols,
                    };
                    const updatedStyles = {
                      ...currentComponent.styles,
                      default: {
                        ...(currentComponent.styles?.default || {}),
                        gridTemplateColumns: `repeat(${cols}, 1fr)`,
                      },
                    };
                    updateComponent(currentComponent.id, {
                      props: updatedProps,
                      styles: updatedStyles,
                    });
                  }
                }}
                onBlur={(e) => {
                  // Ensure value is valid on blur
                  const cols = parseInt(e.target.value, 10);
                  if (isNaN(cols) || cols < 1 || cols > 12) {
                    e.target.value = String(
                      currentComponent.props?.columns ?? 3
                    );
                  }
                }}
                min={1}
                max={12}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Rows</label>
              <input
                type="number"
                value={String(currentComponent.props?.rows ?? 1)}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const inputValue = e.target.value;
                  if (inputValue === "") {
                    // Allow empty input temporarily
                    return;
                  }
                  const rows = parseInt(inputValue, 10);
                  if (!isNaN(rows) && rows >= 1 && rows <= 12) {
                    // Update both props and styles in a single call to avoid race conditions
                    const updatedProps = {
                      ...currentComponent.props,
                      rows: rows,
                    };
                    const updatedStyles = {
                      ...currentComponent.styles,
                      default: {
                        ...(currentComponent.styles?.default || {}),
                        gridTemplateRows: `repeat(${rows}, 1fr)`,
                      },
                    };
                    updateComponent(currentComponent.id, {
                      props: updatedProps,
                      styles: updatedStyles,
                    });
                  }
                }}
                onBlur={(e) => {
                  // Ensure value is valid on blur
                  const rows = parseInt(e.target.value, 10);
                  if (isNaN(rows) || rows < 1 || rows > 12) {
                    e.target.value = String(currentComponent.props?.rows ?? 1);
                  }
                }}
                min={1}
                max={12}
                className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Gap</label>
              <Input
                value={currentComponent.props?.gap ?? "1rem"}
                onChange={(e) => {
                  handlePropChange("gap", e.target.value);
                  handleStyleChange("default", "gap", e.target.value);
                }}
                placeholder="1rem"
              />
            </div>
          </div>
        );
      case "video":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Video URL
              </label>
              <Input
                value={currentComponent.props.src || ""}
                onChange={(e) => handlePropChange("src", e.target.value)}
                placeholder="https://example.com/video.mp4"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Poster Image (Thumbnail)
              </label>
              <Input
                value={currentComponent.props.poster || ""}
                onChange={(e) => handlePropChange("poster", e.target.value)}
                placeholder="https://example.com/poster.jpg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Width</label>
                <Input
                  value={currentComponent.props.width || ""}
                  onChange={(e) => {
                    handlePropChange("width", e.target.value);
                    handleStyleChange("default", "width", e.target.value);
                  }}
                  placeholder="100%, 800px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Height</label>
                <Input
                  value={currentComponent.props.height || ""}
                  onChange={(e) => {
                    handlePropChange("height", e.target.value);
                    handleStyleChange("default", "height", e.target.value);
                  }}
                  placeholder="auto, 450px"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.controls || false}
                  onChange={(e) =>
                    handlePropChange("controls", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Show Controls</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.autoplay || false}
                  onChange={(e) =>
                    handlePropChange("autoplay", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Autoplay</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.loop || false}
                  onChange={(e) => handlePropChange("loop", e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Loop</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.muted || false}
                  onChange={(e) => handlePropChange("muted", e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Muted</span>
              </label>
            </div>
          </div>
        );
      case "carousel":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Autoplay</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.autoplay || false}
                  onChange={(e) =>
                    handlePropChange("autoplay", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Enable autoplay</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Autoplay Interval (ms)
              </label>
              <Input
                type="number"
                value={currentComponent.props.interval || 3000}
                onChange={(e) =>
                  handlePropChange("interval", parseInt(e.target.value) || 3000)
                }
                placeholder="3000"
                min={1000}
                step={500}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Show Indicators
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.showIndicators !== false}
                  onChange={(e) =>
                    handlePropChange("showIndicators", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Display navigation indicators</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Show Arrows
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentComponent.props.showArrows !== false}
                  onChange={(e) =>
                    handlePropChange("showArrows", e.target.checked)
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Display navigation arrows</span>
              </label>
            </div>
          </div>
        );
      case "container":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Display Type
              </label>
              <select
                value={defaultStyles.display || "block"}
                onChange={(e) =>
                  handleStyleChange("default", "display", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="block">Block</option>
                <option value="flex">Flex</option>
                <option value="grid">Grid</option>
                <option value="inline-block">Inline Block</option>
                <option value="inline">Inline</option>
              </select>
            </div>
            {defaultStyles.display === "flex" && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Flex Direction
                </label>
                <select
                  value={defaultStyles.flexDirection || "row"}
                  onChange={(e) =>
                    handleStyleChange(
                      "default",
                      "flexDirection",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 "
                >
                  <option value="row">Row</option>
                  <option value="column">Column</option>
                  <option value="row-reverse">Row Reverse</option>
                  <option value="column-reverse">Column Reverse</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Gap</label>
              <Input
                value={defaultStyles.gap || ""}
                onChange={(e) =>
                  handleStyleChange("default", "gap", e.target.value)
                }
                placeholder="1rem, 20px"
              />
            </div>
          </div>
        );
      case "spacer":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Height</label>
              <Input
                value={currentComponent.props.height || "2rem"}
                onChange={(e) => {
                  handlePropChange("height", e.target.value);
                  handleStyleChange("default", "height", e.target.value);
                }}
                placeholder="2rem, 50px, 10vh"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Width</label>
              <Input
                value={currentComponent.props.width || ""}
                onChange={(e) => {
                  handlePropChange("width", e.target.value);
                  handleStyleChange("default", "width", e.target.value);
                }}
                placeholder="100%, auto"
              />
            </div>
          </div>
        );
      case "dynamic-snippet":
        return (
          <div className="space-y-4">
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center">
              <p className="text-sm text-gray-600 mb-2">
                Configure this dynamic snippet in the <strong>Data</strong> tab
              </p>
              <p className="text-xs text-gray-500">
                Select data source, filters, and rendering style
              </p>
            </div>
          </div>
        );
      default:
        return (
          <div className="text-sm text-gray-500">
            No content options available
          </div>
        );
    }
  };

  const renderStyleTab = () => {
    if (!currentComponent) return null;
    return (
      <div className="space-y-4">
        <StyleInputWithReset
          label="Background Color"
          value={defaultStyles.backgroundColor}
          onChange={(value) =>
            handleStyleChange("default", "backgroundColor", value)
          }
          onReset={() =>
            handleStyleChange("default", "backgroundColor", undefined)
          }
        >
          <div className="flex gap-2">
            <Input
              type="color"
              value={defaultStyles.backgroundColor || "#ffffff"}
              onChange={(e) =>
                handleStyleChange("default", "backgroundColor", e.target.value)
              }
              className="w-16 h-10 p-1"
            />
            <Input
              value={defaultStyles.backgroundColor || "#ffffff"}
              onChange={(e) =>
                handleStyleChange("default", "backgroundColor", e.target.value)
              }
              placeholder="#ffffff"
            />
          </div>
        </StyleInputWithReset>
        <StyleInputWithReset
          label="Text Color"
          value={defaultStyles.color}
          onChange={(value) => handleStyleChange("default", "color", value)}
          onReset={() => handleStyleChange("default", "color", undefined)}
        >
          <div className="flex gap-2">
            <Input
              type="color"
              value={defaultStyles.color || "#000000"}
              onChange={(e) =>
                handleStyleChange("default", "color", e.target.value)
              }
              className="w-16 h-10 p-1"
            />
            <Input
              value={defaultStyles.color || "#000000"}
              onChange={(e) =>
                handleStyleChange("default", "color", e.target.value)
              }
              placeholder="#000000"
            />
          </div>
        </StyleInputWithReset>
        <StyleInputWithReset
          label="Padding"
          value={defaultStyles.padding}
          onChange={(value) => handleStyleChange("default", "padding", value)}
          onReset={() => handleStyleChange("default", "padding", undefined)}
          placeholder="1rem"
        />
        <StyleInputWithReset
          label="Margin"
          value={defaultStyles.margin}
          onChange={(value) => handleStyleChange("default", "margin", value)}
          onReset={() => handleStyleChange("default", "margin", undefined)}
          placeholder="1rem"
        />
        <StyleInputWithReset
          label="Border Radius"
          value={defaultStyles.borderRadius}
          onChange={(value) =>
            handleStyleChange("default", "borderRadius", value)
          }
          onReset={() =>
            handleStyleChange("default", "borderRadius", undefined)
          }
          placeholder="0.25rem"
        />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Font Family</label>
            {defaultStyles.fontFamily && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStyleChange("default", "fontFamily", undefined);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                title="Reset to default"
              >
                <FaTimes className="text-xs" />
              </button>
            )}
          </div>
          <FontSelector
            value={
              defaultStyles.fontFamily ||
              "var(--store-font-family, 'Inter', sans-serif)"
            }
            onChange={(e) => {
              const value = e.target.value;
              handleStyleChange("default", "fontFamily", value || undefined);
            }}
            placeholder="Default (Inter - Platform Font)"
            outputFormat="css"
          />
          <p className="text-xs text-gray-500 mt-2 mb-1">
            Or enter a custom font family (e.g., 'Custom Font', sans-serif)
          </p>
          <Input
            value={
              defaultStyles.fontFamily ||
              "var(--store-font-family, 'Inter', sans-serif)"
            }
            onChange={(e) => {
              const value = e.target.value.trim();
              handleStyleChange("default", "fontFamily", value || undefined);
            }}
            placeholder="e.g., 'Custom Font', sans-serif"
          />
        </div>
        <StyleInputWithReset
          label="Font Size"
          value={defaultStyles.fontSize}
          onChange={(value) => handleStyleChange("default", "fontSize", value)}
          onReset={() => handleStyleChange("default", "fontSize", undefined)}
          placeholder="16px"
        />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Font Weight</label>
            {defaultStyles.fontWeight &&
              defaultStyles.fontWeight !== "normal" && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStyleChange("default", "fontWeight", undefined);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                  title="Reset to default"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
          </div>
          <select
            value={defaultStyles.fontWeight || "normal"}
            onChange={(e) =>
              handleStyleChange("default", "fontWeight", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 "
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
            <option value="300">Light</option>
            <option value="500">Medium</option>
            <option value="600">Semi Bold</option>
            <option value="700">Bold</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Opacity</label>
          <div className="flex gap-2 items-center">
            <Input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={
                defaultStyles.opacity !== undefined
                  ? String(defaultStyles.opacity)
                  : "1"
              }
              onChange={(e) =>
                handleStyleChange(
                  "default",
                  "opacity",
                  parseFloat(e.target.value)
                )
              }
              className="flex-1"
            />
            <Input
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={
                defaultStyles.opacity !== undefined
                  ? String(defaultStyles.opacity)
                  : "1"
              }
              onChange={(e) => {
                const value =
                  e.target.value === ""
                    ? undefined
                    : parseFloat(e.target.value);
                handleStyleChange("default", "opacity", value);
              }}
              placeholder="1"
              className="w-20"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Range: 0 (transparent) to 1 (opaque)
          </p>
        </div>
        <div className="border-t pt-4 mt-4">
          <label className="block text-sm font-medium mb-2">
            Tailwind CSS Classes
          </label>
          <Input
            value={currentComponent?.props?.className || ""}
            onChange={(e) => handlePropChange("className", e.target.value)}
            placeholder="e.g., flex items-center gap-4 md:flex-col lg:gap-8"
            className="font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Add Tailwind CSS classes for styling. Use responsive prefixes:{" "}
            <code className="bg-gray-100 px-1 rounded">md:</code> for tablet,{" "}
            <code className="bg-gray-100 px-1 rounded">lg:</code> for desktop.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Example:{" "}
            <code className="bg-gray-100 px-1 rounded">
              text-center py-4 px-2 md:text-left md:py-8 lg:px-16
            </code>
          </p>
        </div>
      </div>
    );
  };

  const renderAnimationTab = () => {
    if (!currentComponent) return null;

    const currentAnimation = currentComponent.animations || {
      type: "none",
      duration: 800,
      delay: 0,
      direction: "up",
      trigger: "onLoad",
      easing: "ease-out",
    };

    const handleAnimationChange = (key: string, value: any) => {
      if (!currentComponent) return;
      const updatedAnimation = {
        ...currentAnimation,
        [key]: value,
      };
      // If type is "none", remove animations
      if (updatedAnimation.type === "none") {
        updateComponent(currentComponent.id, {
          animations: undefined,
        });
      } else {
        updateComponent(currentComponent.id, {
          animations: updatedAnimation,
        });
      }
    };

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Animation Type
          </label>
          <select
            value={currentAnimation.type || "none"}
            onChange={(e) => handleAnimationChange("type", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 "
          >
            <option value="none">None</option>
            <option value="fade">Fade</option>
            <option value="slide">Slide</option>
            <option value="zoom">Zoom</option>
            <option value="bounce">Bounce</option>
            <option value="moveUpDown">Move Up Down</option>
          </select>
        </div>

        {currentAnimation.type !== "none" && (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">Trigger</label>
              <select
                value={currentAnimation.trigger || "onLoad"}
                onChange={(e) =>
                  handleAnimationChange("trigger", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="onLoad">On Load</option>
                <option value="onScroll">On Scroll (when visible)</option>
                <option value="onHover">On Hover</option>
              </select>
            </div>

            {currentAnimation.type === "slide" && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Direction
                </label>
                <select
                  value={currentAnimation.direction || "up"}
                  onChange={(e) =>
                    handleAnimationChange("direction", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 "
                >
                  <option value="up">Up</option>
                  <option value="down">Down</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                Duration (ms)
              </label>
              <Input
                type="number"
                min="100"
                max="5000"
                step="100"
                value={currentAnimation.duration || 800}
                onChange={(e) =>
                  handleAnimationChange(
                    "duration",
                    parseInt(e.target.value) || 800
                  )
                }
                placeholder="800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Delay (ms)
              </label>
              <Input
                type="number"
                min="0"
                max="5000"
                step="100"
                value={currentAnimation.delay || 0}
                onChange={(e) =>
                  handleAnimationChange("delay", parseInt(e.target.value) || 0)
                }
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Easing</label>
              <select
                value={currentAnimation.easing || "ease-out"}
                onChange={(e) =>
                  handleAnimationChange("easing", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 "
              >
                <option value="ease">Ease</option>
                <option value="ease-in">Ease In</option>
                <option value="ease-out">Ease Out</option>
                <option value="ease-in-out">Ease In Out</option>
                <option value="linear">Linear</option>
              </select>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderLayoutTab = () => {
    const textAlign = defaultStyles.textAlign || "left";
    const justifyContent = defaultStyles.justifyContent || "flex-start";
    const alignItems = defaultStyles.alignItems || "flex-start";

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Text Alignment
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStyleChange("default", "textAlign", "left");
              }}
              className={`p-2 border rounded ${
                textAlign === "left"
                  ? "bg-blue-500 text-white"
                  : "border-gray-300"
              }`}
            >
              <FaAlignLeft />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStyleChange("default", "textAlign", "center");
              }}
              className={`p-2 border rounded ${
                textAlign === "center"
                  ? "bg-blue-500 text-white"
                  : "border-gray-300"
              }`}
            >
              <FaAlignCenter />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStyleChange("default", "textAlign", "right");
              }}
              className={`p-2 border rounded ${
                textAlign === "right"
                  ? "bg-blue-500 text-white"
                  : "border-gray-300"
              }`}
            >
              <FaAlignRight />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleStyleChange("default", "textAlign", "justify");
              }}
              className={`p-2 border rounded ${
                textAlign === "justify"
                  ? "bg-blue-500 text-white"
                  : "border-gray-300"
              }`}
            >
              <FaAlignJustify />
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Horizontal Alignment
          </label>
          <select
            value={justifyContent}
            onChange={(e) =>
              handleStyleChange("default", "justifyContent", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 "
          >
            <option value="flex-start">Left</option>
            <option value="center">Center</option>
            <option value="flex-end">Right</option>
            <option value="space-between">Space Between</option>
            <option value="space-around">Space Around</option>
            <option value="space-evenly">Space Evenly</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Vertical Alignment
          </label>
          <select
            value={alignItems}
            onChange={(e) =>
              handleStyleChange("default", "alignItems", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 "
          >
            <option value="flex-start">Top</option>
            <option value="center">Center</option>
            <option value="flex-end">Bottom</option>
            <option value="stretch">Stretch</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Width</label>
          <Input
            value={defaultStyles.width || ""}
            onChange={(e) =>
              handleStyleChange("default", "width", e.target.value)
            }
            placeholder="100%"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Height</label>
          <Input
            value={defaultStyles.height || ""}
            onChange={(e) =>
              handleStyleChange("default", "height", e.target.value)
            }
            placeholder="auto"
          />
        </div>

        <div className="border-t border-gray-200 pt-4 mt-4">
          <label className="block text-sm font-semibold mb-3 text-gray-700">
            Positioning
          </label>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Position Type
            </label>
            <select
              value={defaultStyles.position || "static"}
              onChange={(e) =>
                handleStyleChange("default", "position", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 "
            >
              <option value="static">Static (default)</option>
              <option value="relative">Relative</option>
              <option value="absolute">Absolute</option>
              <option value="fixed">Fixed</option>
              <option value="sticky">Sticky</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {defaultStyles.position === "absolute" ||
              defaultStyles.position === "fixed"
                ? "Use absolute/fixed to layer elements. Parent should be 'relative'."
                : defaultStyles.position === "relative"
                  ? "Use relative to create a positioning context for absolute children."
                  : "Default positioning in document flow."}
            </p>
          </div>

          {(defaultStyles.position === "absolute" ||
            defaultStyles.position === "fixed" ||
            defaultStyles.position === "relative" ||
            defaultStyles.position === "sticky") && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Top</label>
                  <Input
                    value={defaultStyles.top || ""}
                    onChange={(e) =>
                      handleStyleChange("default", "top", e.target.value)
                    }
                    placeholder="0, 10px, 50%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Right
                  </label>
                  <Input
                    value={defaultStyles.right || ""}
                    onChange={(e) =>
                      handleStyleChange("default", "right", e.target.value)
                    }
                    placeholder="0, 10px, 50%"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bottom
                  </label>
                  <Input
                    value={defaultStyles.bottom || ""}
                    onChange={(e) =>
                      handleStyleChange("default", "bottom", e.target.value)
                    }
                    placeholder="0, 10px, 50%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Left</label>
                  <Input
                    value={defaultStyles.left || ""}
                    onChange={(e) =>
                      handleStyleChange("default", "left", e.target.value)
                    }
                    placeholder="0, 10px, 50%"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Z-Index</label>
            <Input
              type="number"
              value={defaultStyles.zIndex || ""}
              onChange={(e) =>
                handleStyleChange(
                  "default",
                  "zIndex",
                  e.target.value ? parseInt(e.target.value) : undefined
                )
              }
              placeholder="0, 1, 10, 100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Higher values appear on top. Use for layering elements (e.g., text
              over image).
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderDataTab = () => {
    if (!currentComponent || currentComponent.type !== "dynamic-snippet") {
      return null;
    }

    const dataSource = (currentComponent.props?.dataSource as DataSourceType) || "products";
    const displayComponent = (currentComponent.props?.displayComponent as DisplayComponentType) || "grid";
    const filters = (currentComponent.props?.filters as IFilterConfig) || {};

    const handleDataSourceChange = (value: DataSourceType) => {
      if (!value) return; // Don't save empty value
      updateComponent(currentComponent.id, {
        props: {
          ...currentComponent.props,
          dataSource: value,
          filters: {}, // Reset filters when data source changes
        },
      });
    };

    const handleDisplayComponentChange = (value: DisplayComponentType) => {
      updateComponent(currentComponent.id, {
        props: {
          ...currentComponent.props,
          displayComponent: value,
        },
      });
    };

    const handleFilterChange = (key: string, value: any) => {
      updateComponent(currentComponent.id, {
        props: {
          ...currentComponent.props,
          filters: {
            ...filters,
            [key]: value,
          },
        },
      });
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Data Source</label>
          <select
            value={dataSource || ""}
            onChange={(e) => {
              const newValue = e.target.value as DataSourceType;
              handleDataSourceChange(newValue);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          >
            <option value="">-- Select Data Source --</option>
            <option value="products">Products</option>
            <option value="categories">Categories</option>
            <option value="plugins">Plugins</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Select the type of content to display dynamically
          </p>
          {!dataSource && (
            <p className="text-xs text-amber-600 mt-1">
              ⚠️ Please select a data source to display content
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Display Style</label>
          <select
            value={displayComponent}
            onChange={(e) => handleDisplayComponentChange(e.target.value as DisplayComponentType)}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          >
            <option value="grid">Grid</option>
            <option value="carousel">Carousel</option>
            <option value="list">List</option>
            <option value="card">Card</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Choose how the content should be rendered
          </p>
        </div>

        {dataSource === "products" && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold">Product Filters</h3>
            
            <div>
              <label className="block text-sm font-medium mb-2">Categories</label>
              {loadingCategories ? (
                <p className="text-sm text-gray-500">Loading categories...</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded p-2">
                  {categories.length === 0 ? (
                    <p className="text-sm text-gray-500">No categories available</p>
                  ) : (
                    categories.map((category) => (
                      <label key={category._id} className="flex items-center gap-2 py-1">
                        <input
                          type="checkbox"
                          checked={(filters.category || []).includes(category._id)}
                          onChange={(e) => {
                            const currentCategories = (filters.category || []) as string[];
                            if (e.target.checked) {
                              handleFilterChange("category", [...currentCategories, category._id]);
                            } else {
                              handleFilterChange(
                                "category",
                                currentCategories.filter((id) => id !== category._id)
                              );
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{category.name}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Sort By</label>
              <select
                value={filters.sortBy || "created-desc"}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="created-desc">Newest First</option>
                <option value="created-asc">Oldest First</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.featured || false}
                  onChange={(e) => handleFilterChange("featured", e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Featured Products Only</span>
              </label>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.inStock !== false}
                  onChange={(e) => handleFilterChange("inStock", e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">In Stock Only</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Min Price</label>
                <Input
                  type="number"
                  value={filters.priceRange?.min || ""}
                  onChange={(e) =>
                    handleFilterChange("priceRange", {
                      ...(filters.priceRange || {}),
                      min: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Price</label>
                <Input
                  type="number"
                  value={filters.priceRange?.max || ""}
                  onChange={(e) =>
                    handleFilterChange("priceRange", {
                      ...(filters.priceRange || {}),
                      max: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  placeholder="1000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Limit</label>
              <Input
                type="number"
                value={filters.limit || 12}
                onChange={(e) => handleFilterChange("limit", parseInt(e.target.value) || 12)}
                min={1}
                max={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum number of items to display
              </p>
            </div>
          </div>
        )}

        {dataSource === "categories" && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-sm font-semibold">Category Filters</h3>
            
            <div>
              <label className="block text-sm font-medium mb-2">Sort By</label>
              <select
                value={filters.sortBy || "name-asc"}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Limit</label>
              <Input
                type="number"
                value={filters.limit || 12}
                onChange={(e) => handleFilterChange("limit", parseInt(e.target.value) || 12)}
                min={1}
                max={100}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get component display name
  const getComponentLabel = (): string => {
    const typeLabels: Record<string, string> = {
      container: "Container",
      heading: "Heading",
      text: "Text",
      image: "Image",
      button: "Button",
      link: "Link",
      grid: "Grid",
      list: "List",
      "ordered-list": "Ordered List",
      quote: "Quote",
      divider: "Divider",
      code: "Code Block",
      card: "Card",
      carousel: "Carousel",
      spacer: "Spacer",
      video: "Video",
      icon: "Icon",
    };
    return (
      typeLabels[currentComponent.type] ||
      currentComponent.type.charAt(0).toUpperCase() +
        currentComponent.type.slice(1)
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`${getComponentLabel()} Properties`}
      position="right"
      size="lg"
      className="bg-white/75"
    >
      <div className="border-b border-gray-200 mb-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab("content");
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeTab === "content"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Content
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab("style");
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeTab === "style"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Style
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab("layout");
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeTab === "layout"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Layout
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab("animation");
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${
              activeTab === "animation"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Animation
          </button>
          {currentComponent.type === "dynamic-snippet" && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab("data");
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === "data"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Data
            </button>
          )}
        </div>
      </div>

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {activeTab === "content" && renderContentTab()}
        {activeTab === "style" && renderStyleTab()}
        {activeTab === "layout" && renderLayoutTab()}
        {activeTab === "animation" && renderAnimationTab()}
        {activeTab === "data" && renderDataTab()}
      </div>
    </Drawer>
  );
};
